/**
 * Custom Tooth icon used as the visual signature for dental practices.
 *
 * Stroke + fill gradient gives it depth without needing a raster asset,
 * and the optional `animated` prop layers a subtle slow pulse on the
 * crown sparkle so the icon catches the eye on the Patients hero card.
 */
export function ToothIcon({
  className = 'h-7 w-7',
  animated = false,
}: {
  className?: string;
  animated?: boolean;
}) {
  return (
    <svg
      viewBox="0 0 64 64"
      className={className}
      aria-hidden="true"
      role="presentation"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="tooth-grad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="55%" stopColor="#F0F9FF" />
          <stop offset="100%" stopColor="#BAE6FD" />
        </linearGradient>
        <linearGradient id="tooth-stroke" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#0EA5E9" />
          <stop offset="100%" stopColor="#0369A1" />
        </linearGradient>
        <radialGradient id="tooth-shine" cx="35%" cy="25%" r="22%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.95" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Tooth body — molar silhouette */}
      <path
        d="M32 6
           C 22 6, 14 11, 14 22
           C 14 28, 17 32, 19 38
           C 20 43, 21 50, 24 55
           C 26 58, 30 58, 32 50
           C 34 58, 38 58, 40 55
           C 43 50, 44 43, 45 38
           C 47 32, 50 28, 50 22
           C 50 11, 42 6, 32 6 Z"
        fill="url(#tooth-grad)"
        stroke="url(#tooth-stroke)"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />

      {/* Crown shine */}
      <ellipse
        cx="26"
        cy="18"
        rx="6"
        ry="9"
        fill="url(#tooth-shine)"
        className={
          animated ? 'origin-center [animation:tooth-shine_2.6s_ease-in-out_infinite]' : ''
        }
      />

      {/* Sparkle */}
      <g
        className={animated ? '[animation:tooth-sparkle_2.6s_ease-in-out_infinite]' : ''}
        transform="translate(40 12)"
      >
        <path d="M0 -5 L1 -1 L5 0 L1 1 L0 5 L-1 1 L-5 0 L-1 -1 Z" fill="#FCD34D" opacity="0.95" />
      </g>

      {animated && (
        <style>{`
          @keyframes tooth-shine {
            0%, 100% { opacity: 0.7; }
            50%      { opacity: 1; transform: translate(0, -1px); }
          }
          @keyframes tooth-sparkle {
            0%, 100% { transform: translate(40px, 12px) scale(1) rotate(0deg); opacity: 0.95; }
            50%      { transform: translate(40px, 12px) scale(1.25) rotate(45deg); opacity: 1; }
          }
        `}</style>
      )}
    </svg>
  );
}
